import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";

import { Save as SaveIcon } from "@mui/icons-material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import useSSL from "../../../hooks/super/useSSL";
import useAuth from "../../../hooks/useAuth";

import ZeroSSLApiKeyHistoryList from "./component/T_ZeroSSLApiKeyHistory";
import AwsCredentialsHistoryList from "./component/T_AwsCredentialsHistory";
import CertStatusList from "./component/T_CertStatus";
import { CollapseAlert } from "../../../components/pages/common/styled";
import { UserType } from "../../../utils/constants";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import colors from "../../../utils/colors";
import adminAxios from "../../../utils/axios/v1/adminAxios";
import { formatDate } from "../../../utils/format";

// ---------------------------------------------------------------------------
// AWS Credentials card — sibling to the ZeroSSL API Key card on /super/ssl.
//
// Backs onto the CP routes shipped by feat/cp-aws-credentials-store:
//   GET  /api/admin/aws_credentials               -> {access_key_id, default_region, has_secret, ...} | null | 404
//   PUT  /api/admin/aws_credentials               -> save (secret_access_key optional on edits)
//   POST /api/admin/aws_credentials/test          -> {ok: true} | {ok: false, message} | 404
//   POST /api/admin/aws_credentials/history       -> {data: [...], total}
//
// Used by the ACM cert vendor in the install-cert drawer; the secret is
// encrypted at rest by the CP. The card never logs the secret and clears
// the secret out of component state after a successful save.
// ---------------------------------------------------------------------------
const awsCreds = {
  get: () => adminAxios.get("/aws_credentials"),
  save: (body) => adminAxios.put("/aws_credentials", body),
  test: () => adminAxios.post("/aws_credentials/test"),
  history: (body) => adminAxios.post("/aws_credentials/history", body || {}),
};

// us-east-1, eu-west-2, ap-southeast-3, etc. Matches the CP-side Joi
// validation; we mirror it here so the operator gets a synchronous hint.
const REGION_RE = /^[a-z]{2}-[a-z]+-\d$/;

const MASKED_SECRET_PLACEHOLDER = "••••••••••••";

function AwsCredentialsCard({ isSuper }) {
  // Server-side snapshot of the saved row (null while loading, undefined
  // when there's no row yet). Drives the masked-dots treatment + the
  // "Last updated by…" line.
  const [savedRow, setSavedRow] = React.useState(null);

  // Form state. `secretEdited` flips true the moment the operator focuses
  // the secret field with intent to change it; only then do we send a
  // secret_access_key on PUT. Lets a region-only edit skip the secret.
  const [accessKeyId, setAccessKeyId] = React.useState("");
  const [secretValue, setSecretValue] = React.useState("");
  const [secretEdited, setSecretEdited] = React.useState(false);
  const [showSecret, setShowSecret] = React.useState(false);
  const [defaultRegion, setDefaultRegion] = React.useState("us-east-1");

  // History rows + pagination state for the audit table.
  const [historyRows, setHistoryRows] = React.useState(null);
  const [historyTotal, setHistoryTotal] = React.useState(0);
  const [historyFrom, setHistoryFrom] = React.useState(0);
  const [historySize, setHistorySize] = React.useState(5);

  // Test-result status line under the buttons.
  //   null      -> nothing rendered (initial)
  //   {kind:"verified", at}
  //   {kind:"error", message}
  //   {kind:"none"}  -> "No credentials configured yet"
  const [testStatus, setTestStatus] = React.useState(null);

  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [snack, setSnack] = React.useState(null); // { severity, msg }

  const accessKeyRef = React.useRef(null);

  const showSnack = (severity, msg) => setSnack({ severity, msg });

  // Hydrate the form from a GET payload. Accepts either {data: {…}} or
  // the bare object — be defensive against either CP response shape.
  const applyServerRow = React.useCallback((payload) => {
    const row =
      payload && typeof payload === "object" && payload.data !== undefined
        ? payload.data
        : payload;
    if (!row || typeof row !== "object") {
      setSavedRow(undefined);
      setAccessKeyId("");
      setSecretValue("");
      setSecretEdited(false);
      setShowSecret(false);
      setDefaultRegion("us-east-1");
      return;
    }
    setSavedRow(row);
    setAccessKeyId(row.access_key_id || "");
    // Never populate the actual secret into state — the API doesn't
    // return it. Just signal that a secret exists via has_secret.
    setSecretValue("");
    setSecretEdited(false);
    setShowSecret(false);
    setDefaultRegion(row.default_region || "us-east-1");
  }, []);

  const fetchSavedRow = React.useCallback(async () => {
    try {
      const res = await awsCreds.get();
      applyServerRow(res?.data);
    } catch (err) {
      // 404 = "no row yet" — that's an empty form, not an error.
      const status = err?.response?.status || err?.status;
      if (status === 404) {
        applyServerRow(null);
      } else {
        applyServerRow(null);
        const msg =
          err?.response?.data?.message || err?.message || "Failed to load AWS credentials";
        showSnack("error", msg);
      }
    }
  }, [applyServerRow]);

  const fetchHistory = React.useCallback(async (size, from) => {
    setHistoryRows(null);
    try {
      const res = await awsCreds.history({ from, size });
      const body = res?.data;
      const list = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body)
          ? body
          : [];
      const total = Number.isInteger(body?.total)
        ? body.total
        : list.length;
      setHistoryRows(list);
      setHistoryTotal(total);
      setHistoryFrom(from);
      setHistorySize(size);
    } catch (err) {
      // 404 here = no history endpoint reachable yet OR no history.
      // Treat as empty + don't snackbar — the empty section will hide.
      setHistoryRows([]);
      setHistoryTotal(0);
    }
  }, []);

  React.useEffect(() => {
    fetchSavedRow();
    fetchHistory(5, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validation. Mirrors the CP Joi contract:
  //   access_key_id: 16-128 chars
  //   secret_access_key: 16-128 chars (only when sent)
  //   default_region: matches REGION_RE
  const accessKeyError = React.useMemo(() => {
    if (!accessKeyId) return "Access Key ID is required";
    if (accessKeyId.length < 16 || accessKeyId.length > 128)
      return "Access Key ID must be 16-128 characters";
    return null;
  }, [accessKeyId]);

  const secretError = React.useMemo(() => {
    if (!secretEdited) return null; // not being changed -> no validation
    if (!secretValue) return "Secret Access Key is required";
    if (secretValue.length < 16 || secretValue.length > 128)
      return "Secret Access Key must be 16-128 characters";
    return null;
  }, [secretEdited, secretValue]);

  const regionError = React.useMemo(() => {
    if (!defaultRegion) return "Default region is required";
    if (!REGION_RE.test(defaultRegion))
      return "Region must look like us-east-1, eu-west-2, ap-southeast-3";
    return null;
  }, [defaultRegion]);

  // Dirty = any form value differs from the saved row, OR the operator
  // started typing a new secret. For a brand-new (no saved row) form,
  // any non-empty value is dirty.
  const isDirty = React.useMemo(() => {
    if (!savedRow) {
      return !!(accessKeyId || secretValue || (defaultRegion && defaultRegion !== "us-east-1"));
    }
    if (accessKeyId !== (savedRow.access_key_id || "")) return true;
    if (defaultRegion !== (savedRow.default_region || "us-east-1")) return true;
    if (secretEdited && secretValue.length > 0) return true;
    return false;
  }, [savedRow, accessKeyId, defaultRegion, secretValue, secretEdited]);

  const canSave =
    isSuper &&
    isDirty &&
    !saving &&
    !accessKeyError &&
    !regionError &&
    !secretError &&
    // On brand-new rows, the operator must supply a secret. On edits,
    // the secret stays optional (region-only edits are fine).
    (savedRow ? true : !!secretValue && secretEdited);

  const handleSave = async () => {
    if (!canSave) return;
    const body = {
      access_key_id: accessKeyId,
      default_region: defaultRegion,
    };
    if (secretEdited && secretValue) {
      body.secret_access_key = secretValue;
    }
    setSaving(true);
    try {
      const res = await awsCreds.save(body);
      // Refetch authoritative state + history.
      applyServerRow(res?.data);
      // Clear unmasked secret out of state immediately on success.
      setSecretValue("");
      setSecretEdited(false);
      setShowSecret(false);
      showSnack("success", "Saved");
      // Stale test status — the saved creds may differ from what was
      // tested. Clear so the operator has to click "Test" again.
      setTestStatus(null);
      // Refetch the row from the GET endpoint too — PUT may not return
      // the full row in every CP rev.
      await fetchSavedRow();
      await fetchHistory(historySize, 0);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Save failed";
      showSnack("error", msg);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await awsCreds.test();
      const body = res?.data || {};
      if (body.ok === true) {
        setTestStatus({ kind: "verified", at: new Date().toISOString() });
      } else {
        setTestStatus({
          kind: "error",
          message: body.message || "AWS rejected the credentials",
        });
      }
    } catch (err) {
      const status = err?.response?.status || err?.status;
      if (status === 404) {
        setTestStatus({ kind: "none" });
      } else {
        const body = err?.response?.data || {};
        // CP returns {ok:false, message} on 400 — surface that verbatim.
        if (body && body.ok === false && body.message) {
          setTestStatus({ kind: "error", message: body.message });
        } else {
          const msg = body?.message || err?.message || "Test failed";
          setTestStatus({ kind: "error", message: msg });
        }
      }
    } finally {
      setTesting(false);
    }
  };

  const handleHistoryPage = (size, from) => fetchHistory(size, from);

  // Secret field handling -------------------------------------------------
  // When a row exists and the operator hasn't started editing, we render
  // a placeholder of bullet dots and treat focus/keystroke as "start
  // editing", which clears the placeholder + flips secretEdited.
  const beginEditingSecret = () => {
    if (!secretEdited) {
      setSecretEdited(true);
      setSecretValue("");
    }
  };
  const handleSecretChange = (e) => {
    if (!secretEdited) setSecretEdited(true);
    setSecretValue(e.target.value);
  };
  const handleSecretFocus = () => {
    // Don't auto-clear on focus alone — let the operator decide. We do
    // however want the field to be focusable + editable, so just allow
    // the next change/keypress to trigger beginEditingSecret.
  };

  const hasSavedSecret = !!(savedRow && savedRow.has_secret);
  const showMaskedPlaceholder = hasSavedSecret && !secretEdited;

  // Render ---------------------------------------------------------------
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 600,
          color: colors.neutral[900],
          mb: 1,
        }}
      >
        AWS Credentials
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          color: colors.neutral[600],
          mb: 4,
        }}
      >
        Used by the ACM cert vendor. Layerd issues certs through this AWS account; the secret is
        encrypted at rest.
      </Typography>

      <Stack spacing={3} sx={{ maxWidth: 720 }}>
        {/* Access Key ID */}
        <Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: colors.neutral[700],
              mb: 1,
            }}
          >
            Access Key ID
          </Typography>
          <TextField
            fullWidth
            inputRef={accessKeyRef}
            placeholder="AKIA…"
            value={accessKeyId}
            onChange={(e) => setAccessKeyId(e.target.value)}
            disabled={!isSuper || saving}
            error={!!accessKeyError && (accessKeyId.length > 0 || isDirty)}
            helperText={
              accessKeyError && (accessKeyId.length > 0 || isDirty) ? accessKeyError : " "
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 40,
                borderRadius: "8px",
                fontSize: 14,
                "& fieldset": { borderColor: colors.neutral[300] },
                "&:hover fieldset": { borderColor: colors.primary[300] },
                "&.Mui-focused fieldset": { borderColor: colors.primary[300] },
              },
            }}
          />
        </Box>

        {/* Secret Access Key */}
        <Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: colors.neutral[700],
              mb: 1,
            }}
          >
            Secret Access Key
          </Typography>
          <TextField
            fullWidth
            type={showSecret ? "text" : "password"}
            placeholder={
              showMaskedPlaceholder ? MASKED_SECRET_PLACEHOLDER : "Paste your AWS secret access key"
            }
            value={showMaskedPlaceholder ? "" : secretValue}
            onChange={handleSecretChange}
            onFocus={handleSecretFocus}
            onKeyDown={(e) => {
              // Backspace / Delete / typing on the masked placeholder
              // begins editing.
              if (showMaskedPlaceholder) beginEditingSecret();
            }}
            disabled={!isSuper || saving}
            error={!!secretError}
            helperText={secretError || (hasSavedSecret && !secretEdited ? "Saved. Clear + re-paste to change." : " ")}
            InputProps={{
              endAdornment: secretEdited ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showSecret ? "Hide secret" : "Show secret"}
                    onClick={() => setShowSecret((v) => !v)}
                    edge="end"
                    size="small"
                  >
                    {showSecret ? (
                      <VisibilityOff sx={{ fontSize: 18, color: colors.neutral[600] }} />
                    ) : (
                      <Visibility sx={{ fontSize: 18, color: colors.neutral[600] }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 40,
                borderRadius: "8px",
                fontSize: 14,
                "& fieldset": { borderColor: colors.neutral[300] },
                "&:hover fieldset": { borderColor: colors.primary[300] },
                "&.Mui-focused fieldset": { borderColor: colors.primary[300] },
              },
            }}
          />
        </Box>

        {/* Default Region */}
        <Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: colors.neutral[700],
              mb: 1,
            }}
          >
            Default Region
          </Typography>
          <TextField
            fullWidth
            placeholder="us-east-1"
            value={defaultRegion}
            onChange={(e) => setDefaultRegion(e.target.value)}
            disabled={!isSuper || saving}
            error={!!regionError && defaultRegion.length > 0}
            helperText={regionError && defaultRegion.length > 0 ? regionError : " "}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 40,
                borderRadius: "8px",
                fontSize: 14,
                "& fieldset": { borderColor: colors.neutral[300] },
                "&:hover fieldset": { borderColor: colors.primary[300] },
                "&.Mui-focused fieldset": { borderColor: colors.primary[300] },
              },
            }}
          />
        </Box>

        {/* Action row */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            onClick={handleTest}
            disabled={!isSuper || testing}
            startIcon={
              testing ? (
                <CircularProgress size={16} thickness={5} />
              ) : (
                <CheckCircleOutlineIcon style={{ width: 18, height: 18 }} />
              )
            }
            sx={{
              height: 36,
              borderRadius: "8px",
              borderColor: colors.neutral[300],
              color: colors.neutral[800],
              fontSize: 14,
              fontWeight: 400,
              textTransform: "none",
              px: 3,
              "&:hover": {
                borderColor: colors.primary[300],
                backgroundColor: colors.neutral[50],
              },
            }}
          >
            {testing ? "Testing…" : "Test credentials"}
          </Button>
          <PrimaryButton
            onClick={handleSave}
            startIcon={<SaveIcon style={{ width: 20, height: 20 }} />}
            disabled={!canSave}
          >
            {saving ? "Saving…" : "Save"}
          </PrimaryButton>
        </Stack>

        {/* Status line — green tick / red cross / muted info */}
        {testStatus ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {testStatus.kind === "verified" ? (
              <>
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 18, color: "#0f7a42" }}
                />
                <Typography sx={{ fontSize: 13, color: "#0f7a42" }}>
                  Credentials verified · {formatDate(testStatus.at)}
                </Typography>
              </>
            ) : testStatus.kind === "error" ? (
              <>
                <ErrorOutlineIcon sx={{ fontSize: 18, color: "#A11228" }} />
                <Typography sx={{ fontSize: 13, color: "#A11228" }}>
                  {testStatus.message}
                </Typography>
              </>
            ) : (
              <>
                <InfoOutlinedIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
                <Typography sx={{ fontSize: 13, color: colors.neutral[600] }}>
                  No credentials configured yet — save first
                </Typography>
              </>
            )}
          </Box>
        ) : null}

        {/* Last updated by line */}
        {savedRow && (savedRow.updated_at || savedRow.updated_by) ? (
          <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
            Last updated by {savedRow.updated_by || "—"}
            {savedRow.updated_at ? ` · ${formatDate(savedRow.updated_at)}` : ""}
          </Typography>
        ) : null}
      </Stack>

      {/* Change history — hidden when there's nothing to show */}
      {historyRows && historyRows.length > 0 ? (
        <Box sx={{ mt: 4, maxWidth: 800 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: colors.neutral[800],
              mb: 2,
            }}
          >
            Change history
          </Typography>
          <AwsCredentialsHistoryList
            rows={historyRows}
            total={historyTotal}
            from={historyFrom}
            size={historySize}
            onPageChange={handleHistoryPage}
          />
        </Box>
      ) : null}

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnack(null)}
          severity={snack?.severity || "info"}
          variant="filled"
          sx={{ fontSize: 13 }}
        >
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function SASSL() {
  const { getApiKeyHistory, apiSize, insertApiKey, getCertificateHistory, certSize, errMsg, setErr } = useSSL();
  const { isAuthenticated, adminUserType } = useAuth();

  const apikeyRef = React.useRef(null);
  const [apiKeyValue, setApiKeyValue] = React.useState("");
  const saveApiKey = () => {
    if (apiKeyValue === null || apiKeyValue === undefined || apiKeyValue === "") {
      apikeyRef.current.focus();
      return;
    }
    insertApiKey(apiKeyValue);
    setApiKeyValue("");
  };
  const changeApiKey = (e) => {
    setApiKeyValue(e.target.value);
  };

  React.useEffect(() => {
    if (isAuthenticated) {
      getApiKeyHistory(apiSize, 0);
      getCertificateHistory(certSize, 0);
    }
    return () => setErr(null);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const [errOpen, setErrOpen] = React.useState(false);
  React.useEffect(() => {
    if (errMsg) setErrOpen(true);
  }, [errMsg]);

  const isSuper = [UserType.SUPER_ADMIN].includes(adminUserType);

  return (
    <React.Fragment>
      <Helmet title="SA SSL" />
      <PageHeader
        title="SSL Management"
        description="Configure SSL certificates and API keys"
      />
      <Box sx={{ mb: 4 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      <Stack spacing={6}>
        <Box>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: colors.neutral[900],
              mb: 4,
            }}
          >
            Zero SSL API Key Configuration
          </Typography>
          <Stack direction="row" spacing={3} alignItems="center" sx={{ maxWidth: 600 }}>
            <TextField
              fullWidth
              placeholder="Zero SSL Api Key"
              required
              value={apiKeyValue}
              onChange={changeApiKey}
              inputRef={apikeyRef}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "8px",
                  fontSize: 14,
                  "& fieldset": {
                    borderColor: colors.neutral[300],
                  },
                  "&:hover fieldset": {
                    borderColor: colors.primary[300],
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary[300],
                  },
                },
              }}
            />
            <PrimaryButton
              onClick={saveApiKey}
              startIcon={<SaveIcon style={{ width: 20, height: 20 }} />}
              disabled={!isSuper}
            >
              Save
            </PrimaryButton>
          </Stack>
        </Box>
        <Box sx={{ maxWidth: 800 }}>
          <ZeroSSLApiKeyHistoryList />
        </Box>

        {/* AWS Credentials — sibling card, drives the ACM cert vendor.
            Shipped in feat/super-admin-aws-credentials-card. */}
        <AwsCredentialsCard isSuper={isSuper} />
      </Stack>

      <Box sx={{ mt: 8 }}>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 600,
            color: colors.neutral[900],
            mb: 4,
          }}
        >
          SSL Provision Status
        </Typography>
        <CertStatusList />
      </Box>
    </React.Fragment>
  );
}
export default SASSL;
